import { useLocation, useNavigate, useParams } from 'react-router-dom'
import * as Menu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown, GitCompare } from 'lucide-react'
import { useAthletes, useMe } from '@/api/hooks'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { AthleteDot } from '@/components/ui/AthleteDot'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/lib/cn'

/** The switcher's Compare option: not an athlete slug, so it can never collide with one. */
const COMPARE = '__compare'

export interface AthleteSwitcherProps {
  /** Phone top bar: one button with the current athlete that opens a menu of the others. */
  compact?: boolean
}

/**
 * Compare is the switcher's last option ("Both") on desktop and its menu's last item on the phone
 * (simplify pass, 2026-11-03: it left the rail).
 * Segmented control that swaps the `:slug` in the current path, keeping the rest (and query).
 * The list is already limited to who the viewer may see; their own athlete is marked "You".
 */
export function AthleteSwitcher({ compact = false }: AthleteSwitcherProps) {
  const { data: athletes, isLoading } = useAthletes()
  const { data: me } = useMe()
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  if (isLoading) return <Skeleton className={cn('h-9', compact ? 'w-28' : 'w-40')} />
  if (!athletes || athletes.length === 0) return null

  const currentSlug = slug ?? athletes[0]?.slug ?? ''
  const current = athletes.find((a) => a.slug === currentSlug) ?? athletes[0]
  const isMine = (id: string) => athletes.length > 1 && id === me?.athleteId

  const handleChange = (nextSlug: string) => {
    if (nextSlug === COMPARE) {
      navigate('/compare')
      return
    }
    const next = slug
      ? location.pathname.replace(`/a/${slug}`, `/a/${nextSlug}`)
      : `/a/${nextSlug}/today`
    navigate({ pathname: next, search: location.search })
  }

  if (compact && current) {
    const label = (
      <>
        <AthleteDot color={current.color} />
        <span className="truncate">{current.name}</span>
      </>
    )
    if (athletes.length === 1) {
      return (
        <span className="inline-flex h-9 max-w-full items-center gap-2 px-1 text-body font-semibold text-ink">
          {label}
        </span>
      )
    }
    return (
      <Menu.Root>
        <Menu.Trigger
          aria-label={`Switch athlete, showing ${current.name}`}
          className={cn(
            'inline-flex h-9 max-w-full items-center gap-2 rounded-full border border-line bg-surface-2 pl-3 pr-2 text-small font-semibold text-ink',
            'transition-[background-color,transform] duration-150 active:scale-[0.97] data-[state=open]:bg-surface-pop',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          {label}
          <ChevronDown className="size-4 shrink-0 text-ink-3" aria-hidden="true" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            align="start"
            sideOffset={8}
            collisionPadding={12}
            className="gd-pop z-50 min-w-56 rounded-lg border border-line bg-surface-pop p-1.5 shadow-pop"
          >
            <Menu.Label className="px-2.5 pb-1.5 pt-1 text-caption text-ink-3">Athlete</Menu.Label>
            <Menu.RadioGroup value={currentSlug} onValueChange={handleChange}>
              {athletes.map((a) => (
                <Menu.RadioItem
                  key={a.slug}
                  value={a.slug}
                  className="flex min-h-11 cursor-default select-none items-center gap-2.5 rounded-sm px-2.5 text-body text-ink outline-none data-[highlighted]:bg-[color-mix(in_oklab,var(--ink)_8%,transparent)]"
                >
                  <AthleteDot color={a.color} />
                  <span className="truncate">{a.name}</span>
                  {isMine(a.id) && <span className="text-caption text-ink-3">You</span>}
                  <Menu.ItemIndicator className="ml-auto pl-3 text-accent">
                    <Check className="size-4" aria-hidden="true" />
                  </Menu.ItemIndicator>
                </Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
            <Menu.Separator className="mx-1 my-1 h-px bg-line" />
            <Menu.Item
              onSelect={() => navigate('/compare')}
              className="flex min-h-11 cursor-default select-none items-center gap-2.5 rounded-sm px-2.5 text-body text-ink outline-none data-[highlighted]:bg-[color-mix(in_oklab,var(--ink)_8%,transparent)]"
            >
              <GitCompare className="size-4 shrink-0 text-ink-2" aria-hidden="true" />
              Compare athletes
            </Menu.Item>
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <SegmentedControl
        aria-label="Switch athlete"
        value={location.pathname === '/compare' ? COMPARE : currentSlug}
        onChange={handleChange}
        options={[
          ...athletes.map((a) => ({
            value: a.slug,
            label: isMine(a.id) ? (
              <>
                {a.name}
                <span className="ml-1.5 text-caption font-normal text-ink-3">You</span>
              </>
            ) : (
              a.name
            ),
            icon: <AthleteDot color={a.color} />,
          })),
          ...(athletes.length > 1
            ? [
                {
                  value: COMPARE,
                  label: 'Compare',
                  icon: <GitCompare className="size-3.5" aria-hidden="true" />,
                },
              ]
            : []),
        ]}
      />
    </div>
  )
}
